import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button, Input, Table, Tag, Switch, Select, Flex, Form, Tooltip } from "antd";
import { EyeInvisibleOutlined, EyeOutlined, SearchOutlined, DeleteOutlined, CheckOutlined, CloseOutlined, EditFilled, LaptopOutlined } from "@ant-design/icons";
import type { TableColumnsType } from "antd";
import CustomTable from "../../components/CustomTable";
import ConfirmationModal from "../../components/ConfirmationModal";
import UserModal from "./UserModal";
import SessionsModal from "./SessionsModal";
import { Container, ActionBtnWrapper } from "./styles";
import { selectUsers } from "../../redux/features/user/userSlice";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { createAdminUser, updateAdminUser, getAllAdminUser, deleteAdminUser, getAllRolesConfig } from "../../redux/features/user/userAsyncThunk";
import { RequestResults } from "../../redux/common/RequestStatus";
import { AdminUser } from "../../redux/features/user/types";
import { StyledButton } from "../../components/styles";
import { toast } from "react-toastify";
import { CSVLink } from "react-csv";
import { TableRowSelection } from "antd/es/table/interface";
import { selectAuthUserInfo, selectUserRole } from "../../redux/features/auth/selectors";
import { convertUtcToLocal } from "../../utils/helpers/convertUtcToLocal";
import { checkHasPermission } from "../../utils/helpers/checkHasPermission";
import { permissions } from "../../utils/constants/permissions";
import { ActivationStatus } from "../../types";

const Users: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data: adminUsers, status, loading, counts } = useAppSelector(selectUsers);

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [form] = Form.useForm();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeactivationConfirm, setShowDeactivationConfirm] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentLimit, setCurrentLimit] = useState<number>(10);
  const [searchField, setSearchField] = useState<string | undefined>();
  const [searchValue, setSearchValue] = useState<string | undefined>();
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string | undefined>();
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<AdminUser[]>([]);
  const [isSessionsModalOpen, setIsSessionsModalOpen] = useState(false);
  const [sessionUser, setSessionUser] = useState<AdminUser | null>(null);

  const authUserInfo = useAppSelector(selectAuthUserInfo);
  const userRole = useAppSelector(selectUserRole);

  const addKeyProperty = (data: AdminUser[]) => {
    return data.map((item: AdminUser) => ({ ...item, key: item.id }));
  };

  const onSelectChange = (newSelectedRowKeys: any, selectedRows: AdminUser[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
    const newSelectedUsers = selectedRows.filter((row) => !selectedUsers.some((existing) => existing.id === row.id));
    const updatedSelectedUsers = [...selectedUsers.filter((user) => newSelectedRowKeys.includes(user.id)), ...newSelectedUsers];
    setSelectedUsers(updatedSelectedUsers);
  };

  const rowSelection: TableRowSelection<AdminUser> = {
    selectedRowKeys,
    onChange: onSelectChange,

    preserveSelectedRowKeys: true,
    selections: [Table.SELECTION_ALL, Table.SELECTION_NONE],
  };

  const handlePageChange = (page: number) => setCurrentPage(page);
  const handleLimitChange = (limit: number) => {
    setCurrentLimit(limit);
    setCurrentPage(1);
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handleSearchFieldChange = (value: string | null) => {
    setSearchField(value || undefined);
    setCurrentPage(1);
  };

  const handleExpand = (record: AdminUser) => {
    const newExpandedRowKeys = expandedRowKeys.includes(record.id) ? expandedRowKeys.filter((key) => key !== record.id) : [...expandedRowKeys, record.id];
    setExpandedRowKeys(newExpandedRowKeys);
  };

  const handleEditClick = (user: AdminUser) => {
    setIsEditMode(true);
    setCurrentUser(user);
    form.setFieldsValue({ ...user });
    setIsModalOpen(true);
  };

  const handleAddUserClick = () => {
    setIsEditMode(false);
    setCurrentUser(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (isEditMode && currentUser) {
        await dispatch(updateAdminUser({ id: currentUser.id, data: values })).unwrap();
      } else {
        await dispatch(createAdminUser({ data: values })).unwrap();
      }
      setIsModalOpen(false);
      form.resetFields();
    } catch (error: any) {
      console.error("Validation failed:", error);
      toast.error(error || "Failed user update, please try again");
    }
  };


  const hasPermission = useCallback(
    (permissionKey: string) => checkHasPermission(userRole, permissionKey),
    [userRole]
  );

  const isAddPermission = useMemo(() => hasPermission(permissions.AddEditUsers), [hasPermission]);
  const isEditPermission = useMemo(() => hasPermission(permissions.AddEditUsers), [hasPermission]);
  const isViewPermission = useMemo(() => hasPermission(permissions.ViewUsers), [hasPermission]);
  const isDeletePermission = useMemo(() => hasPermission(permissions.DeleteUsers), [hasPermission]);
  const isExportPermission = useMemo(() => hasPermission(permissions.ExportUsers), [hasPermission]);
  const isViewSessionsPermission = useMemo(() => hasPermission(permissions.ViewUserSessions), [hasPermission]);

  const handleOpenSessionsModal = (user: AdminUser) => {
    setSessionUser(user);
    setIsSessionsModalOpen(true);
  };

  const handleCloseSessionsModal = () => {
    setIsSessionsModalOpen(false);
    setSessionUser(null);
  };


  const handleStatusChangeConfirm = async () => {
    try {
      if (currentUser) {
        const updatedStatus = currentUser.status === ActivationStatus.Active ? ActivationStatus.Inactive : ActivationStatus.Active;
        await dispatch(
          updateAdminUser({
            id: currentUser.id,
            data: {
              ...currentUser,
              status: updatedStatus,
            },
          })
        ).unwrap();
        toast.success(`User ${updatedStatus === ActivationStatus.Active ? "activated" : "deactivated"} successfully.`);
        setShowDeactivationConfirm(false);
        setCurrentUser(null);
      }
    } catch (error: any) {
      console.error("Status change failed:", error);
      toast.error(error || "Failed to change user status.");
    }
  };

  const columns: TableColumnsType<AdminUser> = useMemo(() => {
    return [
      { title: "#", key: "index", render: (_, __, index) => `#${index + 1 + (currentPage - 1) * currentLimit}` },
      { title: "First Name", dataIndex: "firstName", key: "firstName" },
      { title: "Last Name", dataIndex: "lastName", key: "lastName" },
      { title: "Phone Number", dataIndex: "phone", key: "phone" },
      { title: "Email", dataIndex: "email", key: "email" },
      { title: "Role", dataIndex: "roleName", key: "roleName" },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (status) => <Tag color={status === ActivationStatus.Active ? "green" : "red"}>{status === ActivationStatus.Active ? "Active" : "Inactive"}</Tag>,
      },
      {
        title: "Last Login",
        dataIndex: "loginAt",
        key: "loginAt",
        render: (loginAt) => <div>{loginAt ? convertUtcToLocal(loginAt, "MM/DD/YYYY hh:mm A") : "-"}</div>,
      },
      {
        title: "Action",
        key: "operation",
        render: (_, record: AdminUser) => (
          <ActionBtnWrapper>
            {isEditPermission && <Switch
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
              checked={record.status === ActivationStatus.Active}
              onChange={() => {
                setCurrentUser({ ...record, role: record?.role });
                setShowDeactivationConfirm(true);
              }}
              disabled={record?.id === authUserInfo?.id}
            />}
            {isViewSessionsPermission && (
              <Tooltip title="View Sessions">
                <Button
                  size="small"
                  shape="circle"
                  type="link"
                  icon={<LaptopOutlined />}
                  onClick={() => handleOpenSessionsModal(record)}
                />
              </Tooltip>
            )}
            {isEditPermission && <Button size="small" shape="circle" type="link" icon={<EditFilled />} onClick={() => handleEditClick({ ...record })} disabled={record?.id === authUserInfo?.id} />}
            {isDeletePermission && <Button
              size="small"
              shape="circle"
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                setCurrentUser(record);
                setShowDeleteConfirm(true);
              }}
              disabled={record?.id === authUserInfo?.id}
            />}
            {isViewPermission && !isAddPermission && !isEditPermission && <Button size="small" shape="circle" type="link" icon={<EyeOutlined />} onClick={() => handleEditClick({ ...record })} disabled={record?.id === authUserInfo?.id} />}
          </ActionBtnWrapper>
        ),
      },
      // {
      //     title: " ",
      //     key: "expand",
      //     render: (_, record) => <Button size="small" shape="circle" icon={expandedRowKeys.includes(record.id) ? <EyeInvisibleOutlined /> : <EyeOutlined />} onClick={() => handleExpand(record)} />,
      //     align: "center",
      // },
    ];
  }, [adminUsers, authUserInfo, isAddPermission, isEditPermission, isDeletePermission, isViewPermission, isExportPermission, isViewSessionsPermission]);

  const exportCSV = () => {
    return selectedUsers.map((row) => ({
      Name: row.firstName,
      Email: row.email,
      Role: row.roleName,
      Status: row.status === ActivationStatus.Active ? "Active" : "Inactive",
      "Last Login": row.loginAt ? convertUtcToLocal(row.loginAt, "MM/DD/YYYY hh:mm A") : "-",
    }));
  };

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchValue(searchValue), 800);
    return () => clearTimeout(handler);
  }, [searchValue]);

  useEffect(() => {
    dispatch(
      getAllAdminUser({
        offset: (currentPage - 1) * currentLimit,
        limit: currentLimit,
        searchField,
        searchValue: debouncedSearchValue?.trim(),
      })
    );
    dispatch(getAllRolesConfig());
  }, [dispatch, currentPage, currentLimit, searchField, debouncedSearchValue]);

  const handleDeleteConfirm = async () => {
    try {
      if (currentUser?.id) {
        await dispatch(deleteAdminUser({ id: currentUser?.id })).unwrap();
        setShowDeleteConfirm(false);
        setCurrentUser(null);
        await dispatch(getAllAdminUser({ offset: (currentPage - 1) * currentLimit, limit: currentLimit, searchField, searchValue: debouncedSearchValue?.trim() }));
      }
    } catch (error: any) {
      console.error("Delete failed:", error);
      toast.error(error || "Delete user failed");
    }
  };

  const handleCloseUserModal = () => {
    form.resetFields();
    setIsModalOpen(false);
  };

  return (
    <Container>
      <Flex wrap gap={9} justify="space-between" align="middle">
        <Flex wrap gap={9} align="center">
          <Input
            placeholder="Search for User"
            size="large"
            prefix={<SearchOutlined />}
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            style={{ minWidth: 150 }}
            size="large"
            options={[
              { value: "admin.firstName", label: "First Name" },
              { value: "admin.lastName", label: "Last Name" },
              { value: "admin.email", label: "Email" },
            ]}
            allowClear
            onChange={handleSearchFieldChange}
            value={searchField}
            placeholder="Search by"
          />
        </Flex>
        <Flex wrap gap={9} align="center">
          {isExportPermission && <StyledButton type="primary" disabled={!selectedRowKeys.length}>
            <CSVLink data={exportCSV()} filename="users.csv" target="_blank">
              Export ({selectedUsers.length})
            </CSVLink>
          </StyledButton>}
          {isAddPermission && <StyledButton onClick={handleAddUserClick} type="primary">
            Add new user
          </StyledButton>}
        </Flex>
      </Flex>
      <CustomTable<AdminUser>
        rowSelection={rowSelection}
        columns={columns}
        dataSource={addKeyProperty(adminUsers)}
        loading={status === RequestResults.pending}
        // expandable={{ expandedRowRender: () => null, expandedRowKeys, onExpand: (expanded, record) => handleExpand(record) }}
        pagination={{
          current: currentPage,
          total: counts,
          pageSize: currentLimit,
          showSizeChanger: true,
          onChange: handlePageChange,
          onShowSizeChange: (_, size) => handleLimitChange(size),
        }}
      />
      <UserModal loading={loading} isEditMode={isEditMode} isModalOpen={isModalOpen} onCancel={() => handleCloseUserModal()} handleSave={handleSave} form={form} hasAddPermission={isAddPermission} hasEditPermission={isEditPermission} hasViewPermission={isViewPermission} />

      {showDeleteConfirm && (
        <ConfirmationModal
          handleSave={handleDeleteConfirm}
          isModalOpen={showDeleteConfirm}
          onCancel={() => setShowDeleteConfirm(false)}
          title="Confirm Delete"
          message="Are you sure you want to delete this user?"
          confirmButtonText="Delete"
          loading={loading}
        />
      )}

      {showDeactivationConfirm && (
        <ConfirmationModal
          hasAddPermission={isAddPermission}
          hasEditPermission={isEditPermission}
          hasViewPermission={isViewPermission}
          handleSave={handleStatusChangeConfirm}
          isModalOpen={showDeactivationConfirm}
          onCancel={() => setShowDeactivationConfirm(false)}
          title={`Confirm ${currentUser?.status === ActivationStatus.Active ? "Deactivation" : "Activation"}`}
          message={`Are you sure you want to ${currentUser?.status === ActivationStatus.Active ? "deactivate" : "activate"} this user?`}
          confirmButtonText={currentUser?.status === ActivationStatus.Active ? "Deactivate" : "Activate"}
          loading={loading}
        />
      )}

      {isSessionsModalOpen && sessionUser && (
        <SessionsModal
          isModalOpen={isSessionsModalOpen}
          onCancel={handleCloseSessionsModal}
          adminId={sessionUser.id}
          adminName={`${sessionUser.firstName} ${sessionUser.lastName}`}
        />
      )}
    </Container>
  );
};

export default Users;
